import { type ClassValue, clsx } from "clsx"
import { format } from "date-fns"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Parse a DateOnly field from the server correctly
 * DateOnly fields are returned as UTC dates but should be treated as local dates
 * @param dateString - The date string from the server (e.g., "2025-06-02T00:00:00+00:00")
 * @returns Date object representing the local date, or null if invalid
 */
export function parseDateOnly(dateString: string | null | undefined): Date | null {
  if (!dateString) return null
  
  // Extract just the date part (YYYY-MM-DD) from the string
  const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return null
  
  const y = Number(match[1])
  const m = Number(match[2])
  const d = Number(match[3])
  // Create a new Date object with local timezone (month is 0-indexed in JS)
  const date = new Date(y, m - 1, d)
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) {
    return null
  }
  return date
}

/**
 * Extracts the date part (YYYY-MM-DD) and appends T00:00:00
 * Browsers parse the result as local timezone
 */
export function normalizeUTCDateOnlyToLocal(dateString: string | null | undefined): string | null {
  if (!dateString) return null
  const datePart = dateString.split('T')[0]
  return `${datePart}T00:00:00`
}

/**
 * The locale decides which of the two dollar currencies gets marked: en-CA renders CAD as
 * "$100,000.00" and USD as "US$100,000.00", and en-US does the reverse. Plain numbers format
 * identically in both, so the number and integer cases share the constant.
 */
const FORMATTING_LOCALE = "en-CA"

/**
 * How every grid cell, group total and chart tooltip turns a value into display text. Shared rather
 * than per-component so the same figure cannot read two ways in two places.
 *
 * @param currencyCode the row's own currency, for currency-typed values. Undefined formats as a
 * bare number: there is no default currency, since any default asserts one the data does not carry.
 */
export function formatCellValue(value: any, type?: string, currencyCode?: string): string {
  if (value == null || value === "") return "—"
  // Defensive: a few synced text fields carry the literal string "null"/"undefined" — treat as empty.
  if (typeof value === "string" && (value.trim().toLowerCase() === "null" || value.trim().toLowerCase() === "undefined")) return "—"
  switch (type) {
    case "currency":
      // No code means no symbol, never a default one.
      return new Intl.NumberFormat(FORMATTING_LOCALE, currencyCode
        ? { style: "currency", currency: currencyCode, minimumFractionDigits: 2 }
        : { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      ).format(value)
    case "percent":
      return `${Number(value).toFixed(1)}%`
    case "date":
      try { const d = parseDateOnly(String(value)); return d ? format(d, "MMM d, yyyy") : String(value) } catch { return String(value) }
    case "boolean":
      return value ? "Yes" : "No"
    case "number":
      return Number(value).toLocaleString(FORMATTING_LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    case "integer":
      return Number(value).toLocaleString(FORMATTING_LOCALE, { maximumFractionDigits: 0 })
    default:
      return String(value)
  }
}
